import axios from 'axios'
import { isEqual } from 'lodash'
import { useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation, TFunction } from 'react-i18next'
import { TextEditorType } from '../../control/textEditor/TextEditor'
import useLoadingStore from '../../stores/useLoadingStore'
import ConfigAPI from '../../constant/ConfigAPI'
import { useSelector } from 'react-redux'
import { RootStateType } from '../../redux/reducer/rootReducer'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

type ParamServerSideType = {
    pageNumber: number
    rowPerPage: number
    sortField: string
    sortType: string
    keyword: string
    groupId: string
}

type ExternalLinkType = {
    name: string
    url: string
}

export interface Form {
    title?: string
    coverImage?: any[]
    content?: TextEditorType
    additionalImage?: any[]
    attachment?: any[]
    externalLink?: ExternalLinkType[]
}

export const validate = async (values: Form, t: TFunction<'translation'>) => {
    const errors: Partial<Form> = {}
    if (!values.title) {
        errors.title = `${t('LABEL.TEXT_REQUIRED')}`
    }
    return errors
}

const initial = {
    remove: undefined,
    index: 0,
    value: '0',
}

const useContent = () => {
    const itemData = useRef<{ remove?: (index: number) => void; index: number; value: string }>(initial)
    const valueSubmitted = useRef<Form>()
    const [initialData, setInitialData] = useState<CMSContentFormType>({})
    const [isDuplicate, setIsDuplicate] = useState<boolean>(false)
    const [openDialogBack, setOpenDialogBack] = useState<boolean>(false)
    const [openDialogSave, setOpenDialogSave] = useState<boolean>(false)
    const [openDialogOk, setOpenDialogOk] = useState<boolean>(false)
    const [alertMessageOk, setAlertMessageOk] = useState<string>('')
    const [openDialogError, setOpenDialogError] = useState<boolean>(false)
    const [alertMessageError, setAlertMessageError] = useState<string>('')
    const [statusCode, setStatusCode] = useState<number>()
    const [actionType, setActionType] = useState<string>('')
    const [data, setData] = useState<ContentType[]>([])
    const [totalItem, setTotalItem] = useState<number>(0)
    const [paramServerSide, setParamServerSide] = useState<ParamServerSideType>({
        pageNumber: 1,
        rowPerPage: 10,
        sortField: '',
        sortType: '',
        keyword: '',
        groupId: '',
    })
    const { t } = useTranslation()
    const history = useHistory()
    const loadingStore = useLoadingStore()
    const { isLoggedIn } = useSelector(({ profile }: RootStateType) => profile)

    useEffect(() => {
        if (isLoggedIn) {
            callServiceList()
        }
    }, [isLoggedIn])

    const callServiceList = async () => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/content/manage/list`
            const result = await axios.get(url, ConfigAPI)
            if (result) {
                setTotalItem(result.data.length)
                setData(
                    result.data.map((item: ContentType) => ({
                        contentId: item.contentId,
                        contentTypeId: item.contentTypeId,
                        contentTypeName: item.contentTypeName,
                        title: item.title,
                        updateDate: item.updatedDateTime
                            ? dayjs.utc(item.updatedDateTime).format('YYYY-MM-DD HH:mm')
                            : '-',
                    })),
                )
            }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired('/backoffice/content-management')
                    //callServiceReLogin('/backoffice/content-management')
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceGetById = async (id: number) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/content/manage/${id}`
            const result = await axios.get(url, ConfigAPI)
            if (result.data) {
                setStatusCode(200)
                setInitialData(result.data)
            }
            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired()
                    //callServiceReLogin()
                } else {
                    setStatusCode(500)
                    setOpenDialogError(true)
                    setAlertMessageError(t('ALERT.CONTENT.NOT_RETRIEVE_DATA'))
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceEdit = async (id: number, data: Form) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/content/manage/${id}`
            const extLinks = data.externalLink?.filter((item: ExternalLinkType) => item.name && item.url)

            let htmlValue = ''
            if (data.content?.value?.trim()) {
                htmlValue = data.content?.value?.trim()
            } else if (data.content?.htmlTag?.trim()) {
                htmlValue = data.content?.htmlTag?.trim()
            }

            let replaceBody = htmlValue.slice(-2) === '\n' ? '' : htmlValue.slice(-2)
            const reqParams = {
                title: data.title,
                body: htmlValue.replace(/.{0,2}$/, replaceBody) ? data.content?.htmlTag.replace(/.{0,2}$/, '') : '',
                externalLinks:
                    extLinks && extLinks?.length > 0
                        ? extLinks?.map((item: ExternalLinkType) => ({
                              displayText: item.name,
                              urlPath: item.url,
                          }))
                        : [],
            }

            const result = await axios.put(url, reqParams, ConfigAPI)

            if (result.data.message === 'success') {
                setStatusCode(200)
                setAlertMessageOk(t('ALERT.CONTENT.SAVE_SUCCESS'))

                // manage cover image
                if (data.coverImage && data.coverImage?.length > 0) {
                    manageCoverImage(id, data.coverImage, 'COVER')
                } else if (initialData.coverImage?.fileId) {
                    callServiceAttachmentDelete(initialData.coverImage?.fileId)
                }

                // manage additional images
                if (data.additionalImage && data.additionalImage?.length > 0) {
                    manageFiles(id, data.additionalImage, 'IMAGE', initialData.pictures)
                } else if (initialData.pictures && initialData.pictures?.length > 0) {
                    // initialData.pictures.forEach(
                    //     (item: FileType) => item.fileId && callServiceAttachmentDelete(item.fileId),
                    // )
                    for (const item of initialData.pictures) {
                        if (item.fileId) {
                            callServiceAttachmentDelete(item.fileId)
                        }
                    }
                }

                // manage files
                if (data.attachment && data.attachment?.length > 0) {
                    manageFiles(id, data.attachment, 'PDF', initialData.pdfLinks)
                } else if (initialData.pdfLinks && initialData.pdfLinks?.length > 0) {
                    // initialData.pdfLinks.forEach(
                    //     (item: FileType) => item.fileId && callServiceAttachmentDelete(item.fileId),
                    // )
                    for (const item of initialData.pdfLinks) {
                        if (item.fileId) {
                            callServiceAttachmentDelete(item.fileId)
                        }
                    }
                }
            } else if (result.data.message === 'duplicated') {
                setStatusCode(200)
                setIsDuplicate(true)
                setAlertMessageOk(t('ALERT.CONTENT.SAVE_DUPLICATED'))
            } else {
                setStatusCode(200)
                setAlertMessageOk(result.data.message)
            }
            setOpenDialogOk(true)

            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    // reLoginForTokenExpired()
                    //callServiceReLogin()
                } else {
                    setStatusCode(500)
                    setOpenDialogError(true)
                    setAlertMessageError(t('ALERT.CONTENT.SAVE_NOT_SUCCESS'))
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceAttachmentAdd = async (id: number, formData: any) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/attachment/cms/${getServiceAddAttachmentName(id)}`
            const result = await axios.post(url, formData, ConfigAPI)
            return { result }
        } catch (error) {
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceAttachmentDelete = async (fileId: number) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/attachment/cms/${fileId}`
            const result = await axios.delete(url, ConfigAPI)
            return { result }
        } catch (error) {
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const getServiceAddAttachmentName = (id: number) => {
        if (id === 1) {
            // ข้อกำหนดและเงื่อนไขการให้บริการ
            return 'termsandconds'
        } else if (id === 2) {
            // วิธีการติดตั้ง และรับชม
            return 'howto'
        } else if (id === 3) {
            // ข้อมูลช่องรายการ
            return 'channels'
        } else if (id === 4) {
            // แผนการขยายโครงข่ายฯ
            return 'plan'
        } else if (id === 5) {
            // ช่วยเหลือ
            return 'help'
        } else {
            return ''
        }
    }

    const uploadFile = async (id: number, files: any[], fileType: AttachmentRequestType) => {
        try {
            let uploadFile = await Promise.all(
                files.map(async (file: any, index: number) => {
                    const formData = new FormData()
                    formData.append('file', file)
                    formData.append('fileType', fileType)

                    const { result } = await callServiceAttachmentAdd(id, formData)
                    return result
                }),
            )

            return uploadFile
        } catch (error) {
            return { error }
        }
    }

    const manageCoverImage = (id: number, files: any[], fileType: AttachmentRequestType) => {
        const duplicateFile = files.filter((file) => file.fileId === initialData.coverImage?.fileId)
        if (duplicateFile.length === 0) {
            if (initialData.coverImage?.fileId) {
                callServiceAttachmentDelete(initialData.coverImage?.fileId)
            }
            uploadFile(id, files, fileType)
        } else if (files.length > 0) {
            uploadFile(id, files, fileType)
        }
    }

    const manageFiles = (id: number, files: any[], fileType: AttachmentRequestType, initialFiles: FileType[] = []) => {
        // ลบไฟล์เก่า
        const dataFileIdList = files?.filter((item) => item.fileId)?.map((item: FileType) => item.fileId)
        const deleteFiles = initialFiles?.filter((file: FileType) => !dataFileIdList?.includes(file.fileId))
        if (deleteFiles && deleteFiles.length > 0) {
            //deleteFiles.forEach((item: FileType) => item.fileId && callServiceAttachmentDelete(item.fileId))

            for (const item of deleteFiles) {
                if (item.fileId) {
                    callServiceAttachmentDelete(item.fileId)
                }
            }
        }

        // เพิ่มไฟล์ใหม่
        const initialDataFileIdList = initialFiles
            ?.filter((item: FileType) => item.fileId)
            ?.map((item: FileType) => item.fileId)
        const newFiles = files.filter((file) => !initialDataFileIdList?.includes(file.fileId))
        if (newFiles.length > 0) {
            const addFiles = newFiles.filter((item: FileType) => !item?.fileId)
            if (addFiles.length > 0) {
                uploadFile(id, addFiles, fileType)
            }
        }
    }

    const handleSubmitForm = (values: any) => {
        valueSubmitted.current = values
        setActionType('save')
        setOpenDialogSave(true)
    }

    const handleClickEdit = async (data: any) => {
        history.push(`/backoffice/content-management/${data?.contentId}/edit`)
    }

    const checkIsDirty = (data: any, contentId: number) => {
        let dirty = false

        // check title dirty
        if (initialData.title !== data.title) dirty = true

        if (!dirty && contentId !== 1) {
            // check coverImage dirty
            if (
                (initialData?.coverImage?.urlPath &&
                    (data.coverImage.length === 0 ||
                        (data.coverImage.length > 0 &&
                            initialData?.coverImage?.urlPath.split('/').pop() !== data.coverImage[0]?.name))) ||
                (!initialData?.coverImage && data.coverImage.length > 0)
            )
                dirty = true
        }

        if (!dirty) {
            // check contentHTML dirty
            // const htmlValue = data.content?.value
            //     ? data.content?.value.trim()
            //     : data.content?.htmlTag
            //     ? data.content?.htmlTag.trim()
            //     : ''

            let htmlValue = ''
            if (data.content?.value) {
                htmlValue = data.content?.value.trim()
            } else if (data.content?.htmlTag) {
                htmlValue = data.content?.htmlTag.trim()
            }

            const replaceData = htmlValue.slice(-2) === '\n' ? '' : htmlValue.slice(-2)
            const dataBody = htmlValue.replace(/.{0,2}$/, replaceData)
                ? data.content?.htmlTag.replace(/.{0,2}$/, '')
                : null
            if (initialData.body !== dataBody) dirty = true
        }

        if (!dirty && contentId !== 1) {
            // check additionalImage dirty
            const tempDataAdditionalImage = initialData.pictures?.map((item) => item.displayText)
            const initialDataAdditionalImage = tempDataAdditionalImage ?? []
            const tempAdditionalImage = data.additionalImage?.map((item: ExternalLinkType) => item.name.split('.')?.[0])
            const dataAdditionalImage = tempAdditionalImage ?? []
            if (!isEqual(initialDataAdditionalImage, dataAdditionalImage)) dirty = true
        }

        if (!dirty && contentId !== 1) {
            // check attachmentPDF dirty
            const tempDataAttachment = initialData.pdfLinks?.map((item) => item.displayText)
            const initialDataAttachment = tempDataAttachment ?? []
            const tempAttachment = data.attachment?.map((item: ExternalLinkType) => item.name.split('.')?.[0])
            const dataAttachment = tempAttachment ?? []
            if (!isEqual(initialDataAttachment, dataAttachment)) dirty = true
        }

        if (!dirty && contentId !== 1) {
            // check externalLink dirty
            const tempDataExternalLinks = initialData.externalLinks?.map((item) => ({
                name: item.displayText,
                url: item.urlPath,
            }))
            const initialDataExternalLinks = tempDataExternalLinks ?? []
            const dataExternalLinks = data.externalLink.filter((item: ExternalLinkType) => item.name || item.url)
            if (!isEqual(initialDataExternalLinks, dataExternalLinks)) dirty = true
        }

        return dirty
    }

    const handleClickForceBack = () => {
        history.push('/backoffice/content-management')
    }

    const handleClickBack = (values: any, contentId: number) => {
        // ตอนกดปุ่มยกเลิกที่ form
        if (checkIsDirty(values, contentId)) {
            setOpenDialogBack(true)
        } else {
            setOpenDialogBack(false)
            history.push('/backoffice/content-management')
        }
        setActionType('back')
    }

    const handleClickCancelBack = () => {
        setOpenDialogBack(false)
    }

    const handleClickConfirmBack = () => {
        setOpenDialogBack(false)
        history.push('/backoffice/content-management')
    }

    const handleClickCancelSave = () => {
        setOpenDialogSave(false)
    }

    const handleClickConfirmSave = (id: number) => {
        callServiceEdit(id, valueSubmitted.current as Form)
        setOpenDialogSave(false)
    }

    const handleClickOk = () => {
        setOpenDialogOk(false)
        setOpenDialogError(false)
        if (statusCode === 401) {
            history.push('/login')
        } else if (!isDuplicate && statusCode !== 500) {
            history.push('/backoffice/content-management')
        }
        setIsDuplicate(false)
    }

    const handleClickDeleteFieldArrayItem = (remove: (index: number) => void, index: number, value: string) => {
        itemData.current = { remove, index, value }
        remove(index)
    }

    return {
        onSubmitForm: handleSubmitForm,
        onClickDeleteFieldArrayItem: handleClickDeleteFieldArrayItem,
        onClickEdit: handleClickEdit,
        onClickCancelBack: handleClickCancelBack,
        onClickCancelSave: handleClickCancelSave,
        onClickConfirmBack: handleClickConfirmBack,
        onClickConfirmSave: handleClickConfirmSave,
        onClickForceBack: handleClickForceBack,
        onClickBack: handleClickBack,
        onClickOk: handleClickOk,
        callServiceGetById,
        data,
        totalItem,
        paramServerSide,
        openDialogBack,
        openDialogSave,
        openDialogOk,
        alertMessageOk,
        openDialogError,
        alertMessageError,
        actionType,
    }
}

export default useContent
